/**
 * Data model for the Monthly Cashflow chart (dashboards round 3: calendar-
 * year axis), shared between the Overall dashboard (group scope) and the
 * entity/Personal dashboard.
 *
 * Monthly net cashflow is the month-over-month change in total bank balance
 * (cash basis, from transactions), NOT net margin -- net margin is P&L;
 * cashflow includes financing, intercompany settlements, VAT and timing, so
 * they legitimately diverge. This is why the two are rendered together
 * (bars + line) rather than one replacing the other.
 *
 * Bars come from GET /dashboard/cash-runway's calendar_year=true response's
 * `calendar_year_months` array -- Jan-Dec of the current year, the same
 * raw-transaction predicate the Cash Runway card's trailing-6-month
 * avg_monthly_net uses, just re-bucketed to the full calendar year.
 * Every month through the current one is a real (possibly zero) figure --
 * absence of transactions is a genuine net-zero, not a coverage gap, unlike
 * the snapshot-based Bank Balance/Net Assets charts; only months after the
 * current one are `net: null` (no forward projection), and that null MUST
 * stay null through this file -- it renders as an empty bar, never a
 * fabricated zero. The BARS keep the current (possibly partial) month; the
 * AVERAGE (computeAvgNetCashflow) does not -- see that function's own
 * comment. These are two different metrics with two different labels
 * ("Avg net cashflow (YTD, complete months)" on this card vs "Avg net
 * cashflow (last 6 full months)" on the Cash Runway card) precisely because
 * they answer different questions and can legitimately show different
 * numbers -- never assume they must match.
 *
 * The net margin overlay line is read from the Gross Revenue chart's own
 * per-month `_netMargin` field (entityGrossRevenueChart.js /
 * groupGrossRevenueChart.js rows) -- the same P&L predicate, never
 * re-derived. Those rows are already calendar-year (Jan-Dec) shaped too, so
 * every month here has a corresponding month there.
 */

import { monthKeyToLabel } from './forecastChartUtils';

/**
 * @param {object} input
 * @param {object[]} input.calendarYearMonths - cashRunway.calendar_year_months: [{month: 'YYYY-MM', net}], net is null for months after the current one
 * @param {object[]} [input.netMarginRows] - Gross Revenue chart rows carrying `_monthKey` + `_netMargin`
 */
export function buildMonthlyCashflowChartData(input) {
  var calendarYearMonths = input.calendarYearMonths || [];
  var netMarginByMonth = {};
  (input.netMarginRows || []).forEach(function (r) {
    if (r && r._monthKey) netMarginByMonth[r._monthKey] = r._netMargin;
  });

  return calendarYearMonths.map(function (m) {
    return {
      month: monthKeyToLabel(m.month),
      _monthKey: m.month,
      netCashflow: m.net == null ? null : (parseFloat(m.net) || 0),
      netMargin: Object.prototype.hasOwnProperty.call(netMarginByMonth, m.month)
        ? netMarginByMonth[m.month]
        : null,
    };
  });
}

/**
 * Mean of the rendered bars, COMPLETE months only -- the bars themselves
 * still show the current (possibly partial) month, but the average excludes
 * it so a handful of days never gets weighted as a full month (which would
 * silently deflate/inflate the figure as the month progresses). The current
 * month is always the LAST populated bar: every month up to "now" is a real
 * (possibly zero) figure and only months after "now" are null (see
 * buildMonthlyCashflowChartData), so dropping the last populated row drops
 * exactly the current month, never a genuinely-empty past month.
 *
 * `fallback` (default 0) is returned when there are zero complete months to
 * average -- e.g. January, where the only populated bar IS the current
 * month. Callers pass the trailing-6-month cashRunway.avg_monthly_net here
 * so the card shows a real number instead of a manufactured one-month
 * average; this never divides by zero.
 */
export function computeAvgNetCashflow(chartData, fallback) {
  var rows = (chartData || []).filter(function (r) { return r.netCashflow != null; });
  var completeRows = rows.slice(0, rows.length - 1);
  if (completeRows.length === 0) return fallback != null ? fallback : 0;
  var sum = completeRows.reduce(function (s, r) { return s + r.netCashflow; }, 0);
  return sum / completeRows.length;
}
